import type { ClientMessage, ServerMessage } from '../shared/protocol';

export interface ConnectionHandlers {
  onOpen?: () => void;
  onMessage: (msg: ServerMessage) => void;
  onClose?: () => void;
  onError?: () => void;
}

export interface Connection {
  send(msg: ClientMessage): void;
  close(): void;
}

function wsUrl(): string {
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
}

/**
 * Opens a typed WebSocket connection. When `autoReconnect` is set the socket is
 * re-opened after a short delay on unexpected close (used by the game page).
 */
export function connect(handlers: ConnectionHandlers, autoReconnect = false): Connection {
  let ws: WebSocket;
  let closedByUser = false;

  function open(): void {
    ws = new WebSocket(wsUrl());
    ws.addEventListener('open', () => handlers.onOpen?.());
    ws.addEventListener('message', (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data) as ServerMessage;
      } catch {
        return;
      }
      handlers.onMessage(msg);
    });
    ws.addEventListener('close', () => {
      handlers.onClose?.();
      if (autoReconnect && !closedByUser) setTimeout(open, 2000);
    });
    ws.addEventListener('error', () => handlers.onError?.());
  }

  open();

  return {
    send(msg: ClientMessage): void {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
    },
    close(): void {
      closedByUser = true;
      ws.close();
    },
  };
}
